import { useQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SpeciesSelectElement_SpeciesGetByIdDocument } from "./queries.generated";
import { SpeciesOption, SpeciesSelectSingleProps } from "./types";
import { useSpeciesSearch } from "./useSpeciesSearch";

export default function SpeciesSelectSingle({
    value,
    onChange,
    disabled,
    label,
    excludeAssignedToResearchTypeId,
    error,
    helperText,
}: SpeciesSelectSingleProps) {
    const { t } = useTranslation("shared");

    const {
        isOpen,
        setOpen,
        inputText,
        setInputText,
        searchText,
        options,
        loading: isSearchLoading,
    } = useSpeciesSearch({ excludeAssignedToResearchTypeId });

    // Fetch species by ID when value is set and not found in options
    const { data: dataValue, loading: isLoadingValue } = useQuery(SpeciesSelectElement_SpeciesGetByIdDocument, {
        variables: { id: value },
        skip: value === undefined || value === "" || options.some(x => x.id === value),
    });

    const loading = isSearchLoading || isLoadingValue;

    const singleSelectOptions: SpeciesOption[] = isOpen
        ? [...options]
        : dataValue?.settingsSpeciesGetById !== undefined
          ? [dataValue?.settingsSpeciesGetById]
          : [];

    const singleSelectValue = singleSelectOptions.find(x => x.id === value) ?? null;

    return (
        <Autocomplete
            noOptionsText={
                searchText.length === 0
                    ? t("SpeciesSelectElement.typeToSearchLabel")
                    : t("SpeciesSelectElement.noOptionsFoundLabel")
            }
            disabled={disabled}
            disableClearable={singleSelectValue !== null}
            filterOptions={x => x}
            getOptionLabel={option => option.name}
            getOptionKey={option => option.id}
            loading={loading}
            inputValue={inputText}
            onOpen={() => setOpen(true)}
            onClose={() => {
                setOpen(false);
                setInputText("");
            }}
            onInputChange={(_, nextValue, reason) => {
                if (reason === "input") {
                    setInputText(nextValue);
                    onChange(value ?? "");
                }
                if (reason === "reset" && !isOpen) {
                    setInputText(nextValue);
                }
            }}
            open={isOpen}
            options={singleSelectOptions}
            onChange={(_, nextValue) => {
                if (nextValue) {
                    onChange(nextValue.id);
                }
            }}
            value={singleSelectValue}
            renderInput={params => <TextField {...params} label={label} error={error} helperText={helperText} />}
        />
    );
}
